import React from 'react';
import { addToCart, saveDealer } from '../api/cart';
import { updateLastServiceView } from '../api/recommendations';
import controlRulesById from '../RecommendationsModalContainer/controlRulesById';
import {
  OpenRecomendationsModalPayload,
  OpenServicesModalPayload,
} from '../types/recommendationsModal.types';
import { extractTextFromArray, pushGtmDataLayer } from '../utils';

export type ServiceData = {
  data?: {
    customer_3m_dealer?: {
      dealer?: string;
    };
    product_id?: string;
    product_sku?: string;
    popup_title?: string;
    popup_subtitle?: string;
    product_name?: string;
    popup_image?: string;
    product_image?: string;
    roduct_sku?: string;
  };
};

export const calculateBackdropPadding = (): number => {
  const headerEl = document.getElementById('headerMainContent');
  const headerHeight = headerEl?.getBoundingClientRect()?.height ?? 0;
  const announcementEl = document.getElementById('announcement');
  const announcementElHeight =
    announcementEl?.getBoundingClientRect()?.height ?? 0;
  return headerHeight + announcementElHeight;
};

export const prepareBannerContent = (
  detail: OpenRecomendationsModalPayload,
  isInRealTimeStock: boolean,
  isServerMessage: boolean,
  textLabels: {
    notAddedToCart: string;
    addedToCart: string;
    shipping: { stock: string; oos: string };
    bannerSubtitleDefaultText: string;
  },
) => {
  const bannerLabel: string | React.JSX.Element = isServerMessage
    ? textLabels.notAddedToCart
    : textLabels.addedToCart;

  let bannerShippingTitle: string = isInRealTimeStock
    ? textLabels.shipping.stock
    : textLabels.shipping.oos;

  let bannerShippingSubtitle: string | null = isInRealTimeStock
    ? null
    : textLabels.bannerSubtitleDefaultText;

  if (isServerMessage) {
    bannerShippingSubtitle = null;
    const serverMessages = extractTextFromArray(detail?.serverMessage ?? '', [
      '\n',
    ]);
    bannerShippingTitle = serverMessages[0];

    if (serverMessages?.[1] != null) {
      bannerShippingSubtitle = serverMessages[1];
    }
  }

  return { bannerLabel, bannerShippingTitle, bannerShippingSubtitle };
};

export const getControlRuleConfig = (
  controlRuleId: string | number | null,
  isBrandDailyLimitReached: boolean,
) => {
  if (isBrandDailyLimitReached) {
    return {
      list_id_suffix: 'cart_item_threshold_error',
      list_name_prefix: 'cart item threshold error',
    };
  }

  return controlRuleId != null
    ? controlRulesById[controlRuleId]
    : {
        list_id_suffix: 'system_rule',
        list_name_prefix: 'System rule',
      };
};

export const formatRecommendedItems = (
  recommendedItems: Record<string, unknown>[],
  availableMaterialsEntityIds: string[],
  itemListId: string,
  itemListName: string,
) => {
  const recommendedItemsEntityIds = recommendedItems?.map(
    (item: Record<string, unknown>) => item.entity_id,
  );

  const pdpTrackingParams = new URLSearchParams(
    `itemListId=${itemListId}&itemListName=${itemListName}`,
  );

  return recommendedItems
    ?.filter((result: Record<string, unknown>) =>
      availableMaterialsEntityIds.includes(String(result.entity_id)),
    )
    ?.map((result: Record<string, unknown>) => ({
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      id: result.entity_id as string | number,
      manufacturer: String(result.name || ''),
      name: String(result.manufacturer || ''),
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      type: (result.categories as string[])
        ?.filter((category: string) => !category.includes('>'))
        ?.join(' ')
        ?.toUpperCase(),
      image: String(result.thumbnailImageUrl || ''),
      isAddedToCart: false,
      isCartLoading: false,
      isInStock: availableMaterialsEntityIds.includes(String(result.entity_id)),
      linkUrl:
        result.url != null
          ? `${window.location.origin}${
              result.url
            }?${pdpTrackingParams.toString()}&index=${
              recommendedItemsEntityIds.indexOf(result.entity_id) + 1
            }`
          : null,
      originalProduct: result,
      itemListId: pdpTrackingParams.get('itemListId'),
      itemListName: pdpTrackingParams.get('itemListName'),
      index: recommendedItemsEntityIds.indexOf(result.entity_id) + 1,
    }));
};

export const sendAnalyticsEvents = (
  detail: OpenRecomendationsModalPayload,
  isInRealTimeStock: boolean,
  isServerMessage: boolean,
  controlRuleId: string | number | null,
  isBrandDailyLimitReached: boolean,
  customer?: { customer_id?: string },
) => {
  if (!isInRealTimeStock) {
    pushGtmDataLayer({ ecommerce: null });
    pushGtmDataLayer({
      list_id: 'sim_oos',
      list_name: 'Out of stock SIM',
      item_list_id: 'sim_oos',
      item_list_name: 'Out of stock SIM',
    });
  }

  if (isServerMessage) {
    pushGtmDataLayer({ ecommerce: null });
    pushGtmDataLayer({
      list_id: 'sim_control_rule',
      list_name: 'Control rule SIM',
      item_list_id: 'sim_control_rule',
      item_list_name: 'Control rule SIM',
    });

    // Send control rule errors
    if (controlRuleId !== null && window.dataLayer) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = detail.serverMessage || '';
      const serverMessageText = tempDiv.textContent || tempDiv.innerText || '';

      pushGtmDataLayer({
        event: 'mb_site_error',
        error_type: 'control_rules',
        error_message: serverMessageText,
        error_interaction: isBrandDailyLimitReached
          ? 'add to cart item limit'
          : 'add to cart',
        error_page: window.location.href,
        user_id: customer?.customer_id,
        event_item_brand: detail?.manufacturer,
        event_item_id: detail?.skus?.join(','),
      });
    }
  }
};

export const determineServiceType = (services: ServiceData): string => {
  if (services?.data?.hasOwnProperty('customer_3m_dealer')) {
    if (services?.data?.customer_3m_dealer?.dealer) {
      return 'ask-for-dealer';
    } else {
      return 'dealer';
    }
  }
  return 'service';
};

export const prepareServiceBannerContent = (
  detail: OpenServicesModalPayload,
  isInRealTimeStock: boolean,
  isServerMessage: boolean,
  textLabels: {
    notAddedToCart: string;
    addedToCart: string;
    shipping: { stock: string; oos: string };
    bannerSubtitleDefaultText: string;
  },
) => {
  let bannerLabel: string | React.JSX.Element = '';
  let bannerShippingTitle: string = isInRealTimeStock
    ? textLabels.shipping.stock
    : textLabels.shipping.oos;
  let bannerShippingSubtitle: string | null = isInRealTimeStock
    ? null
    : textLabels.bannerSubtitleDefaultText;

  if (isServerMessage) {
    bannerShippingSubtitle = null;
  }

  if (detail?.title) {
    bannerLabel = detail.title;
  } else {
    bannerLabel = isServerMessage
      ? textLabels.notAddedToCart
      : textLabels.addedToCart;
  }

  const serverMessages = extractTextFromArray(detail?.serverMessage ?? '', [
    '\n',
  ]);
  bannerShippingTitle = isServerMessage
    ? serverMessages[0]
    : bannerShippingTitle;

  if (isServerMessage && serverMessages?.[1] != null) {
    bannerShippingSubtitle = serverMessages?.[1];
  }

  return { bannerLabel, bannerShippingTitle, bannerShippingSubtitle };
};

export const createServiceProposal = (
  services: ServiceData,
  type: string,
  detail: OpenServicesModalPayload,
  callbacks: {
    handleCloseClick: () => void;
    setProposal: React.Dispatch<React.SetStateAction<any>>;
    handleDealerNameChange: (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    dealerName: React.MutableRefObject<string | null>;
  },
) => {
  const { handleCloseClick, setProposal, handleDealerNameChange, dealerName } =
    callbacks;

  const baseProposal = {
    type,
    dealerName: services?.data?.customer_3m_dealer || null,
    isAddDealerButtonLoading: true,
    onProposalTextFieldChange: handleDealerNameChange,
  };

  switch (type) {
    case 'ask-for-dealer':
      return {
        ...baseProposal,
        noLabel: 'No, I am not',
        yesLabel: 'Yes, I am',
        title: 'Are you still working with this Partner/Supplier?',
        companyName: services?.data?.customer_3m_dealer?.dealer,
        onYesClick: async () => {
          handleCloseClick();
          await saveDealer(services?.data?.customer_3m_dealer?.dealer ?? '');
        },
        onNoClick: async () => {
          setProposal((prevProposal: any) => ({
            ...prevProposal,
            type: 'dealer',
            title: services?.data?.popup_title,
            companyName: services?.data?.product_name,
            noLabel: undefined,
            yesLabel: undefined,
            onYesClick: async () => {
              setProposal((prevProposal: any) => ({
                ...prevProposal,
                isAddDealerButtonLoading: true,
              }));
              const response = await saveDealer(dealerName.current);
              setProposal((prevProposal: any) => ({
                ...prevProposal,
                isAddDealerButtonLoading: false,
                type:
                  response.status === 'success'
                    ? 'dealer-added'
                    : prevProposal?.type,
                subtitle:
                  response.status === 'success'
                    ? 'Your 3M dealer has been added to your profile'
                    : prevProposal?.subtitle,
              }));
            },
          }));
        },
      };

    case 'service':
      return {
        ...baseProposal,
        title: 'Free estimates for flooring and installation',
        subtitle: ['Budgeting', 'Waste factors', 'Lead times'],
        companyName:
          'Diverzify – National commercial flooring and services provider',
        onYesClick: async () => {
          handleCloseClick();
          addToCart(
            [
              {
                id: services?.data?.product_id,
                is_diverzify: true,
                itemListId: 'sm_diverzify',
                itemListName: 'Diverzify',
              },
            ],
            detail.bagProject,
          );
          pushGtmDataLayer({ ecommerce: null });
          pushGtmDataLayer({
            event: 'add_to_cart',
            event_location: 'Service modal',
            list_id: 'sm_diverzify',
            list_name: 'Diverzify',
            ecommerce: {
              currency: 'USD',
              value: 0,
              items: [
                {
                  item_id: services?.data?.product_sku,
                  item_name: 'Flooring Install', // TODO remove hardocde
                  item_brand: 'Diverzify', // TODO remove hardcode
                  item_variant: null,
                  item_list_id: 'sm_diverzify',
                  item_list_name: 'Diverzify',
                  index: 1,
                  quantity: 1,
                  price: 0,
                  item_category: null,
                  item_category2: null,
                  item_category3: null,
                },
              ],
            },
          });
        },
        onNoClick: null,
      };

    case 'dealer':
      return {
        ...baseProposal,
        title: services?.data?.popup_title,
        subtitle: services?.data?.popup_subtitle,
        companyName: services?.data?.product_name,
        onYesClick: async () => {
          setProposal((prevProposal: any) => ({
            ...prevProposal,
            isAddDealerButtonLoading: true,
          }));
          const response = await saveDealer(dealerName.current);
          setProposal((prevProposal: any) => ({
            ...prevProposal,
            isAddDealerButtonLoading: false,
            type:
              response.status === 'success'
                ? 'dealer-added'
                : prevProposal?.type,
            subtitle:
              response.status === 'success'
                ? 'Your 3M dealer has been added to your profile'
                : prevProposal?.subtitle,
          }));
        },
        onNoClick: async () => {
          handleCloseClick();
          await updateLastServiceView();
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('reload-customer-section-data'),
            );
          }, 500);
        },
      };

    default:
      return baseProposal;
  }
};
